import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CalendarCheck,
  CircleDollarSign,
  Clock3,
  Download,
  Receipt,
  TrendingUp,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import { getMyVenues } from '../services/venueService';
import { getOwnerReport } from '../services/reportService';
import { formatCurrency, formatDate, formatTime } from '../utils/formatters';

const CHART_COLORS = ['#0B7F83', '#264D59', '#56B7AC', '#D46C4E', '#F9E07F'];

const philippineDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Manila',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function philippineDateValue() {
  const parts = Object.fromEntries(
    philippineDateFormatter
      .formatToParts(new Date())
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function createDateRange(days) {
  const endValue = philippineDateValue();
  const end = new Date(`${endValue}T00:00:00Z`);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return { start: start.toISOString().slice(0, 10), end: endValue };
}

function payMongoWeeklyWindow(cyclesAgo = 0) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Manila', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date()).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  const now = new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second));
  const cutoff = new Date(now); cutoff.setUTCHours(21, 0, 0, 0);
  cutoff.setUTCDate(cutoff.getUTCDate() - ((cutoff.getUTCDay() - 2 + 7) % 7));
  if (now < cutoff) cutoff.setUTCDate(cutoff.getUTCDate() - 7);
  cutoff.setUTCDate(cutoff.getUTCDate() - (cyclesAgo * 7));
  const end = new Date(cutoff); end.setUTCDate(end.getUTCDate() + 7);
  const dateTime = (value) => `${value.toISOString().slice(0, 10)}T${value.toISOString().slice(11, 19)}`;
  return { start: dateTime(cutoff), end: dateTime(end), startDate: cutoff.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
}

function formatChartDate(date) {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatHours(hours) {
  const value = Number(hours || 0);
  return `${Number.isInteger(value) ? value : value.toFixed(1)}h`;
}

function formatPercent(value) {
  return `${Math.round(Number(value || 0))}%`;
}

function csvCell(value) {
  const stringValue = String(value ?? '');
  return /[",\n]/.test(stringValue) ? `"${stringValue.replaceAll('"', '""')}"` : stringValue;
}

export default function Reports() {
  const [activityType, setActivityType] = useState('All');
  const [venues, setVenues] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [dateRange, setDateRange] = useState(() => createDateRange(30));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentWindow, setPaymentWindow] = useState(null);
  const [payoutCycle, setPayoutCycle] = useState('custom');

  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    if (selectedVenueId) loadReport();
  }, [selectedVenueId, dateRange.start, dateRange.end, paymentWindow?.start, paymentWindow?.end, activityType]);

  async function loadVenues() {
    try {
      const data = await getMyVenues();
      const list = Array.isArray(data) ? data : data?.venues || [];
      setVenues(list);
      if (list.length > 0) setSelectedVenueId(list[0].id || list[0]._id);
    } catch {
      toast.error('Failed to load venues');
    }
  }

  async function loadReport() {
    setLoading(true);
    try {
      const data = await getOwnerReport(selectedVenueId, dateRange.start, dateRange.end, paymentWindow, activityType);
      setReport(data);
    } catch (error) {
      setReport(null);
      toast.error(error.response?.data?.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }

  function setQuickRange(days) {
    setPaymentWindow(null);
    setPayoutCycle('custom');
    setDateRange(createDateRange(days));
  }

  function selectPayoutCycle(value) {
    setPayoutCycle(value);
    if (value === 'custom') {
      setPaymentWindow(null);
      return;
    }
    const window = payMongoWeeklyWindow(Number(value));
    setPaymentWindow({ start: window.start, end: window.end });
    setDateRange({ start: window.startDate, end: window.endDate });
  }

  function exportReport() {
    if (!report) return;

    const summary = report.summary || {};
    const feePolicy = report.feePolicy || {};
    const showOwnerFeeDeductions = Number(summary.feesDeductedFromPayout || 0) > 0;
    const awaitingPayout = Number(summary.awaitingPayout ?? summary.payMongoSettlementPending ?? 0)
      + Number(summary.payoutInProgress ?? summary.freacHeld ?? 0);
    const rows = [
      ['FREAC owner report', report.venue?.name || 'Venue'],
      ['Activity', report.activityType || 'All'],
      ['Payment period', `${dateRange.start} to ${dateRange.end}`],
      [],
      ['Summary'],
      ['Total venue earnings', summary.totalVenueEarnings ?? ((summary.payableByFreac ?? summary.netPayout ?? 0) + (summary.venueDirectCollections || 0))],
      ['Received directly by venue', summary.venueDirectCollections],
      ['Awaiting payout', awaitingPayout],
      ['Paid to venue', summary.paidToVenue ?? summary.ownerReceived],
      ['Booking revenue', summary.bookingRevenue ?? summary.grossCollections],
      ...(showOwnerFeeDeductions
        ? [['Fees deducted from payout', summary.feesDeductedFromPayout]]
        : [['Fee policy', feePolicy.message || 'Only the booking fee is paid by customers; PayMongo fees and VAT are FREAC costs and do not reduce venue earnings.']]),
      ['Paid bookings / registrations', summary.paidBookings],
      ['Payment transactions', summary.transactions],
      ['Cancelled bookings', summary.cancelledBookings],
      ['Booked hours', summary.bookedHours],
      ['Scheduled utilization', `${summary.utilization || 0}%`],
      [],
      ['Daily performance'],
      ['Date', 'Bookings', 'Cancelled', 'Booked hours', 'Booking revenue', 'Received directly', 'Online earnings'],
      ...(report.dailyPerformance || []).map((day) => [day.date, day.bookings, day.cancelled, day.bookedHours, day.bookingRevenue ?? day.grossCollections, day.venueDirectCollections, day.payableByFreac ?? day.netPayout]),
      [],
      ['Court performance'],
      ['Court', 'Bookings', 'Cancelled', 'Booked hours', 'Booking revenue', 'Received directly', 'Online earnings', 'Utilization'],
      ...(report.courtPerformance || []).map((court) => [court.name, court.bookings, court.cancelled, court.bookedHours, court.bookingRevenue ?? court.grossCollections, court.venueDirectCollections, court.payableByFreac ?? court.netPayout, `${court.utilization || 0}%`]),
      [],
      ['Payment methods'],
      ['Method', 'Collection', 'Transactions', 'Booking revenue', ...(showOwnerFeeDeductions ? ['Fees deducted'] : []), 'Online earnings'],
      ...(report.paymentMethods || []).map((method) => [method.paymentMethod, method.collectionChannel === 'VenueDirect' ? 'Received by venue' : 'Collected by FREAC', method.transactions, method.bookingRevenue ?? method.grossCollections, ...(showOwnerFeeDeductions ? [method.feesDeductedFromPayout] : []), method.payableByFreac ?? method.netPayout]),
    ];
    const csv = `\uFEFF${rows.map((row) => row.map(csvCell).join(',')).join('\n')}`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `freac-owner-report-${dateRange.start}-to-${dateRange.end}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const summary = report?.summary || {};
  const dailyPerformance = report?.dailyPerformance || [];
  const courtPerformance = report?.courtPerformance || [];
  const paymentMethods = report?.paymentMethods || [];
  const upcomingBookings = report?.upcomingBookings || [];
  const openPlays = report?.openPlays || {};
  const feePolicy = report?.feePolicy || {};
  const feesDeductedFromPayout = Number(summary.feesDeductedFromPayout || 0);
  const awaitingPayout = Number(summary.awaitingPayout ?? summary.payMongoSettlementPending ?? 0)
    + Number(summary.payoutInProgress ?? summary.freacHeld ?? 0);
  const awaitingPayoutCount = Number(summary.payMongoSettlementPendingCount || 0)
    + Number(summary.freacHeldCount || 0);
  const summaryCards = [
    {
      label: 'Total venue earnings',
      value: formatCurrency(summary.totalVenueEarnings ?? ((summary.payableByFreac ?? summary.netPayout ?? 0) + Number(summary.venueDirectCollections || 0))),
      detail: feesDeductedFromPayout > 0 ? 'Net earnings after venue-absorbed fees' : 'Net earnings for the selected payment period',
      icon: CircleDollarSign,
      color: 'text-[#264D59]',
      background: 'bg-[#E8F4FD]',
    },
    {
      label: 'Awaiting payout',
      value: formatCurrency(awaitingPayout),
      detail: `${awaitingPayoutCount} online earning${awaitingPayoutCount === 1 ? '' : 's'} not yet paid to your venue`,
      icon: WalletCards,
      color: 'text-[#0B7F83]',
      background: 'bg-[#E0F7F4]',
    },
    {
      label: 'Paid to venue',
      value: formatCurrency(summary.paidToVenue ?? summary.ownerReceived),
      detail: `${summary.ownerReceivedCount || 0} completed payout${summary.ownerReceivedCount === 1 ? '' : 's'}`,
      icon: Receipt,
      color: 'text-[#166534]',
      background: 'bg-[#DCFCE7]',
    },
    {
      label: 'Received directly',
      value: formatCurrency(summary.venueDirectCollections),
      detail: 'Cash already held by your venue',
      icon: CircleDollarSign,
      color: 'text-[#166534]',
      background: 'bg-[#DCFCE7]',
    },
    ...(feesDeductedFromPayout > 0 ? [{
      label: 'Booking fees deducted',
      value: formatCurrency(feesDeductedFromPayout),
      detail: 'FREAC booking fees absorbed by your venue',
      icon: Receipt,
      color: 'text-[#B54708]',
      background: 'bg-[#FFF4E5]',
    }] : []),
    {
      label: 'Booked hours',
      value: formatHours(summary.bookedHours),
      detail: `${summary.bookings || 0} active booking${summary.bookings === 1 ? '' : 's'}`,
      icon: Clock3,
      color: 'text-[#D46C4E]',
      background: 'bg-[#FDF1EB]',
    },
    {
      label: 'Scheduled utilization',
      value: formatPercent(summary.utilization),
      detail: summary.availableCourtHours ? `${formatHours(summary.availableCourtHours)} court capacity` : 'Set operating hours to measure capacity',
      icon: TrendingUp,
      color: 'text-[#B7791F]',
      background: 'bg-[#FFF8E1]',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Financial &amp; Performance Report</h1>
          <p className="mt-1 text-sm text-[#64748B]">Track earnings by payment date alongside scheduled venue activity.</p>
        </div>
        <button type="button" onClick={exportReport} disabled={!report || loading} className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50">
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </button>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-medium text-[#64748B]">Venue</label>
            <select value={selectedVenueId} onChange={(event) => setSelectedVenueId(event.target.value)} className="input-field">
              <option value="">Select venue</option>
              {venues.map((venue) => (
                <option key={venue.id || venue._id} value={venue.id || venue._id}>{venue.name}</option>
              ))}
            </select>
          </div>
          <label className="text-xs font-medium text-[#64748B]">Activity
            <select aria-label="Activity" className="input-field mt-1" value={activityType} onChange={e => setActivityType(e.target.value)}><option value="All">All activities</option><option value="CourtBookings">Court Bookings</option><option value="OpenPlay">Open Play</option><option value="StepDance">Step Dance</option></select>
          </label>
          <div>
            <label className="mb-1 block text-xs font-medium text-[#64748B]">Payment start date</label>
            <input type="date" value={dateRange.start} max={dateRange.end} onChange={(event) => { setPaymentWindow(null); setPayoutCycle('custom'); setDateRange((current) => ({ ...current, start: event.target.value })); }} className="input-field" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[#64748B]">Payment end date</label>
            <input type="date" value={dateRange.end} min={dateRange.start} onChange={(event) => { setPaymentWindow(null); setPayoutCycle('custom'); setDateRange((current) => ({ ...current, end: event.target.value })); }} className="input-field" />
          </div>
          <div className="flex gap-2">
            {[1, 7, 30].map((days) => (
              <button key={days} type="button" onClick={() => setQuickRange(days)} className="btn-secondary px-3 py-2 text-xs">{days === 1 ? 'Today' : `Last ${days}d`}</button>
            ))}
            <label hidden className="text-xs font-medium text-[#64748B]">Payout Weekly Cutoff
              <select value={payoutCycle} onChange={(event) => selectPayoutCycle(event.target.value)} className="input-field mt-1 min-w-[170px] py-2 text-xs">
                <option value="0">Current cycle</option>
                <option value="1">Previous cycle</option>
                <option value="2">2 cycles ago</option>
                <option value="3">3 cycles ago</option>
                <option value="custom">Custom dates</option>
              </select>
            </label>
          </div>
        </div>
        {paymentWindow && <div className="mt-3 rounded-xl border border-[#B9E6FE] bg-[#F0F9FF] px-4 py-3 text-xs text-[#026AA2]">Payout cycle: {paymentWindow.start.replace('T', ' ')} to {paymentWindow.end.replace('T', ' ')} PHT. Transactions at or after the ending cutoff move to the next payout.</div>}
        <p className="mt-3 text-xs text-[#64748B]">Financial and performance totals include court bookings, Open Play and Step Dance belonging to this venue account. Player-organised sessions remain available in Booking Details only.</p>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
        </div>
      ) : !selectedVenueId ? (
        <div className="card p-12 text-center">
          <TrendingUp className="mx-auto h-14 w-14 text-[#94A3B8]" />
          <h2 className="mt-4 text-lg font-semibold text-[#1F2937]">Select a venue to get started</h2>
          <p className="mt-1 text-sm text-[#64748B]">Your booking, payout, and court-performance reports will appear here.</p>
        </div>
      ) : !report ? (
        <div className="card p-12 text-center text-sm text-[#64748B]">Unable to load this report. Please try again.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="card p-5">
                <div className="flex items-start gap-4">
                  <div className={`rounded-xl p-3 ${card.background}`}><card.icon className={`h-6 w-6 ${card.color}`} /></div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#64748B]">{card.label}</p>
                    <p className="mt-1 text-2xl font-bold text-[#1F2937]">{card.value}</p>
                    <p className="mt-1 text-xs text-[#64748B]">{card.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {(!feePolicy.venueAbsorbsPaymentFees || summary.hasCustomerPaidFees) && (
            <section className="flex items-start gap-3 rounded-xl border border-[#BFE5DF] bg-[#F0FAF9] p-4 text-sm text-[#285B63]">
              <Receipt className="mt-0.5 h-5 w-5 shrink-0 text-[#0B7F83]" />
              <p>Only the booking fee is paid by customers. PayMongo fees and VAT are FREAC costs, so they do not reduce your venue earnings or payouts.</p>
            </section>
          )}

          {Number(summary.venueDirectCollections || 0) > 0 && (
            <section className="flex items-start gap-3 rounded-xl border border-[#BBE2C6] bg-[#F0FDF4] p-4 text-sm text-[#166534]">
              <CircleDollarSign className="mt-0.5 h-5 w-5 shrink-0" />
              <p>Cash payments are recorded as venue revenue but are already received by your venue, so they are excluded from FREAC payouts.</p>
            </section>
          )}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <section className="card p-6">
              <div className="mb-5 flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-[#0B7F83]" /><div><h2 className="font-semibold text-[#1F2937]">Venue activity</h2><p className="text-xs text-[#64748B]">Bookings and venue-hosted sessions by day</p></div></div>
              {dailyPerformance.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={285}>
                  <BarChart data={dailyPerformance} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={formatChartDate} tick={{ fontSize: 11, fill: '#64748B' }} minTickGap={24} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                    <Tooltip labelFormatter={formatChartDate} formatter={(value, name) => [name === 'Booked hours' ? formatHours(value) : value, name]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="bookings" name="Venue activity" fill="#0B7F83" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="bookedHours" name="Booked hours" fill="#56B7AC" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </section>

            <section className="card p-6">
              <div className="mb-5 flex items-center gap-2"><CircleDollarSign className="h-5 w-5 text-[#0B7F83]" /><div><h2 className="font-semibold text-[#1F2937]">Online earnings trend</h2><p className="text-xs text-[#64748B]">Net platform-collected venue earnings grouped by customer payment date</p></div></div>
              {dailyPerformance.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={285}>
                  <LineChart data={dailyPerformance} margin={{ top: 6, right: 8, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={formatChartDate} tick={{ fontSize: 11, fill: '#64748B' }} minTickGap={24} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(value) => `₱${value}`} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={52} />
                    <Tooltip labelFormatter={formatChartDate} formatter={(value) => formatCurrency(value)} />
                    <Line type="monotone" dataKey="payableByFreac" name="Online earnings" stroke="#0B7F83" strokeWidth={3} dot={false} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </section>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <section className="card p-6 xl:col-span-2">
              <div className="mb-5 flex items-center gap-2"><Receipt className="h-5 w-5 text-[#0B7F83]" /><div><h2 className="font-semibold text-[#1F2937]">Court performance</h2><p className="text-xs text-[#64748B]">Compare bookings and venue-hosted sessions by court.</p></div></div>
              {courtPerformance.length === 0 ? <EmptyChart label="No active courts are available for this venue." /> : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#E2E8F0] text-sm">
                    <thead><tr className="text-left text-xs uppercase tracking-wide text-[#64748B]"><th className="pb-3 pr-4 font-medium">Court</th><th className="pb-3 pr-4 text-right font-medium">Activity</th><th className="pb-3 pr-4 text-right font-medium">Hours</th><th className="pb-3 pr-4 text-right font-medium">Received directly</th><th className="pb-3 text-right font-medium">Online earnings</th></tr></thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {courtPerformance.map((court) => <tr key={court.id}><td className="py-3 pr-4 font-medium text-[#1F2937]">{court.name}</td><td className="py-3 pr-4 text-right text-[#475569]">{court.bookings}</td><td className="py-3 pr-4 text-right text-[#475569]">{formatHours(court.bookedHours)}</td><td className="py-3 pr-4 text-right text-[#166534]">{formatCurrency(court.venueDirectCollections)}</td><td className="py-3 text-right font-semibold text-[#0B7F83]">{formatCurrency(court.payableByFreac ?? court.netPayout)}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="card p-6">
              <div className="mb-4 flex items-center gap-2"><WalletCards className="h-5 w-5 text-[#0B7F83]" /><div><h2 className="font-semibold text-[#1F2937]">Payment mix</h2><p className="text-xs text-[#64748B]">Booking revenue by payment method</p></div></div>
              {paymentMethods.length === 0 ? <EmptyChart label="No completed payments in this period." /> : (
                <>
                  <ResponsiveContainer width="100%" height={170}>
                    <PieChart><Pie data={paymentMethods} dataKey="bookingRevenue" nameKey="paymentMethod" cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3}>{paymentMethods.map((method, index) => <Cell key={method.paymentMethod} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}</Pie><Tooltip formatter={(value) => formatCurrency(value)} /></PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2">{paymentMethods.map((method, index) => <div key={method.paymentMethod} className="flex items-center justify-between gap-3 text-sm"><span className="flex min-w-0 items-center gap-2 text-[#475569]"><i className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} /><span>{method.paymentMethod}<small className="block text-[10px] text-[#64748B]">{method.collectionChannel === 'VenueDirect' ? 'Received by venue' : 'Collected by FREAC'}</small></span></span><span className="font-medium text-[#1F2937]">{formatCurrency(method.bookingRevenue ?? method.grossCollections)}</span></div>)}</div>
                </>
              )}
            </section>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <section className="card p-6 xl:col-span-2">
              <div className="mb-5 flex items-center justify-between"><div className="flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-[#0B7F83]" /><div><h2 className="font-semibold text-[#1F2937]">Upcoming bookings</h2><p className="text-xs text-[#64748B]">Your next six court sessions to prepare for.</p></div></div><span className="text-xs font-medium text-[#64748B]">{upcomingBookings.length} shown</span></div>
              {upcomingBookings.length === 0 ? <EmptyChart label="No upcoming court bookings." /> : <div className="space-y-3">{upcomingBookings.map((booking) => <div key={booking.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#F8FAFC] p-3"><div><p className="font-medium text-[#1F2937]">{booking.playerName || 'Player'} <span className="font-normal text-[#64748B]">· {booking.courtName}</span></p><p className="mt-1 text-xs text-[#64748B]">{formatDate(booking.startTime)} · {formatTime(booking.startTime)} – {formatTime(booking.endTime)}</p></div><div className="text-right"><p className="text-sm font-semibold text-[#1F2937]">{formatCurrency(booking.totalAmount)}</p><p className="mt-1 text-xs capitalize text-[#64748B]">{booking.status}</p></div></div>)}</div>}
            </section>

            <section className="card bg-[#F0FAF9] p-6">
              <div className="flex items-center gap-2"><UsersRound className="h-5 w-5 text-[#0B7F83]" /><div><h2 className="font-semibold text-[#1F2937]">Session activity</h2><p className="text-xs text-[#64748B]">Venue-hosted sessions contribute to this report</p></div></div>
              <div className="mt-6 space-y-4"><MetricRow label="Venue-hosted sessions" value={openPlays.sessions || 0} /><MetricRow label="Venue-hosted registrations" value={openPlays.registrations || 0} /><MetricRow label="Paid registrations" value={openPlays.paidRegistrations || 0} /><MetricRow label="Net payout" value={formatCurrency(openPlays.netPayout)} emphasized />{Number(openPlays.playerOrganizedSessions || 0) > 0 && <MetricRow label="Player-organised sessions*" value={openPlays.playerOrganizedSessions} />}</div>
              {Number(openPlays.playerOrganizedSessions || 0) > 0 && <p className="mt-4 text-xs text-[#64748B]">*Shown in Booking Details; excluded from venue financial and performance totals.</p>}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function EmptyChart({ label = 'No data is available for this period.' }) {
  return <div className="flex h-56 items-center justify-center text-center text-sm text-[#64748B]">{label}</div>;
}

function MetricRow({ label, value, emphasized = false }) {
  return <div className="flex items-center justify-between gap-3 border-b border-[#CBEDE8] pb-3 last:border-0 last:pb-0"><span className="text-sm text-[#475569]">{label}</span><span className={emphasized ? 'text-lg font-bold text-[#0B7F83]' : 'font-semibold text-[#1F2937]'}>{value}</span></div>;
}
